import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  faCheck,
  faCheckDouble,
  faClockRotateLeft,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BanIcon } from "@heroicons/react/outline";
import {
  ConversationContent,
  WhatsAppMessage,
} from "@hilos/types/private-schema";
import MessageRenderPreview from "src/components/MessageRenderPreview";
import useDateFnsFormatIntlLocale from "src/hooks/useDateFnsFormatLocale";
import { WhatsAppMessageStatusEnum } from "src/types/conversation";
import { classNames, userToString } from "../../../Helpers";
import { getTimeFromTimestamp } from "../../../helpers/date";
import { HandleMountConversationContent } from "./Conversation";
import ConversationErrorMessages from "./ConversationErrorMessages";
import DeleteMessage from "./ConversationMessageButton/DeleteMessage";
import ForwardMessage from "./ConversationMessageButton/ForwardMessage";
import ReplyTo from "./ConversationMessageButton/ReplyTo";
import ResendMessage from "./ConversationMessageButton/ResendMessage";
import ConversationMessageMenu from "./ConversationMessageMenu";

interface ConversationMessageContainerProps {
  content: ConversationContent;
  inboxContactId: string;
  message: WhatsAppMessage;
  isFocused: boolean;
  isSubmitting?: boolean;
  showDetails: boolean;
  showConversationEvents: boolean;
  allowReplyTo: boolean;
  onFocusMessage: (messageId: string) => Promise<void>;
  onSelectContextContent: (content: ConversationContent) => void;
  onSelectForwardMessage: (message: WhatsAppMessage) => void;
  onMountConversationContent: HandleMountConversationContent;
}

function ConversationMessageContainer({
  content,
  inboxContactId,
  children,
  message,
  isFocused,
  isSubmitting,
  showDetails,
  showConversationEvents,
  allowReplyTo,
  onFocusMessage,
  onSelectContextContent,
  onSelectForwardMessage,
  onMountConversationContent,
}: PropsWithChildren<ConversationMessageContainerProps>) {
  const { t } = useTranslation();
  const { formatWithLocale } = useDateFnsFormatIntlLocale();
  const isOutbound = message.direction === "OUTBOUND";
  const messageRef = useRef<HTMLDivElement>(null);

  const hasFailedMessage = useMemo(
    () =>
      !message.is_deleted &&
      (message.status === WhatsAppMessageStatusEnum.FAILED ||
        message.status === WhatsAppMessageStatusEnum.RETRY),
    [message]
  );

  const handleFocus = useCallback(() => {
    if (messageRef.current) {
      messageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  useEffect(
    () =>
      onMountConversationContent({
        id: content.id,
        messageId: message.id,
        onFocus: handleFocus,
      }),
    [onMountConversationContent, content.id, message, handleFocus]
  );

  useEffect(() => {
    if (isFocused) {
      handleFocus();
    }
  }, [isFocused, handleFocus]);

  return (
    <>
      {showDetails && (
        <div
          className={classNames(
            "flex px-2 pb-0.5 w-full align-center items-center text-xs text-gray-500",
            isOutbound ? "justify-end text-right" : "justify-start text-left"
          )}
        >
          <div>
            {!isSubmitting && isOutbound && (
              <>
                {message.sent_by
                  ? ` ${userToString(message.sent_by)} @ `
                  : "🤖 @ "}
              </>
            )}
            <span className="">
              {getTimeFromTimestamp(message.timestamp, formatWithLocale)}
            </span>
          </div>

          {!isSubmitting && isOutbound && (
            <div className="ml-1 text-right text-xs">
              {message.status === WhatsAppMessageStatusEnum.RETRY && (
                <FontAwesomeIcon icon={faClockRotateLeft} />
              )}
              {message.status === WhatsAppMessageStatusEnum.SENT && (
                <FontAwesomeIcon icon={faCheck} />
              )}
              {message.status === WhatsAppMessageStatusEnum.DELIVERED && (
                <FontAwesomeIcon icon={faCheckDouble} />
              )}
              {message.status === WhatsAppMessageStatusEnum.READ && (
                <FontAwesomeIcon
                  icon={faCheckDouble}
                  className="text-blue-600"
                />
              )}
            </div>
          )}
        </div>
      )}
      {hasFailedMessage && (
        <div
          className={classNames(
            "flex flex-row items-center mx-2",
            isOutbound ? "justify-end" : "justify-start"
          )}
        >
          <div
            className={classNames(
              "flex max-w-[21rem] min-w-[2rem] space-x-1 align-center items-center text-xs text-red-500",
              isOutbound ? "justify-end text-right" : "justify-start text-left"
            )}
          >
            {message.status === "failed" && (
              <>
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span className="whitespace-pre-wrap flex flex-col gap-1">
                  <ConversationErrorMessages
                    errorCode={message.provider_error_code}
                    errorMessage={message.provider_error_message}
                  />
                  <a
                    href="https://hilos.io/docs/user/advanced/error-messages"
                    rel="noreferrer"
                    target="_blank"
                    className="text-blue-300 underline"
                  >{`${t("why-this-happens", "Why does this happen?")}`}</a>
                </span>
              </>
            )}
            {message.status === "retry" && (
              <>
                <FontAwesomeIcon icon={faClockRotateLeft} />
                <span className="whitespace-pre-wrap flex flex-col gap-1">
                  {t("errors:retrying", "Retrying")}:{" "}
                  <ConversationErrorMessages
                    errorCode={message.provider_error_code}
                    errorMessage={message.provider_error_message}
                  />
                </span>
              </>
            )}
          </div>
        </div>
      )}
      <div
        ref={messageRef}
        className={classNames(
          "flex group mx-2 text-sm flex-row items-center overflow-hidden",
          isOutbound ? "justify-end" : "justify-start"
        )}
      >
        <span
          className={classNames(
            "flex max-w-[21rem] min-w-[2rem] rounded-lg p-2 transition-colors delay-200 duration-500 ease-in-out overflow-hidden",
            isOutbound
              ? `${isFocused ? "bg-indigo-700" : "bg-indigo-400"} text-white`
              : isFocused
              ? "bg-gray-500 text-white"
              : "bg-gray-200 text-gray-600",
            isSubmitting && "inbox-msg-pulse"
          )}
        >
          {message.is_deleted ? (
            <span
              className={classNames(
                "flex flex-row italic",
                isOutbound ? "text-gray-200" : "text-gray-400"
              )}
            >
              <BanIcon className="mr-1 h-4 w-4 self-center" />
              {t("message-is-deleted", "Message has been deleted")}
            </span>
          ) : (
            <div className="flex-col w-full min-w-0">
              {message.context_message && message.context_message && (
                <div className="flex mb-2 w-full overflow-hidden">
                  <button
                    className={classNames(
                      "self-center text-xs rounded-md w-full py-1 px-2 border-l-4 min-w-0",
                      isOutbound
                        ? "border-indigo-500 bg-white bg-opacity-30"
                        : "bg-white bg-opacity-30 border-gray-400 "
                    )}
                    onClick={() =>
                      message.context_message
                        ? onFocusMessage(message.context_message.id)
                        : null
                    }
                  >
                    <MessageRenderPreview
                      message={message.context_message}
                      isPreviewInMessage
                    />
                  </button>
                </div>
              )}
              <div className="relative pr-6 sm:pr-0 w-full min-w-0">
                {children}
                {["sent", "delivered", "read", "new", "received"].includes(
                  message.status
                ) && (
                  <ConversationMessageMenu
                    isFocused={isFocused}
                    isOutbound={isOutbound}
                    items={[
                      (active) =>
                        allowReplyTo && (
                          <ReplyTo
                            active={active}
                            content={content}
                            onSelectContextContent={onSelectContextContent}
                          />
                        ),
                      (active) => (
                        <ForwardMessage
                          active={active}
                          message={message}
                          setSelectedForwardMessage={onSelectForwardMessage}
                        />
                      ),
                    ]}
                  />
                )}
                {hasFailedMessage && (
                  <ConversationMessageMenu
                    isFocused={isFocused}
                    isOutbound={isOutbound}
                    items={[
                      (active) => (
                        <ResendMessage
                          active={active}
                          messageId={message.id}
                          inboxContactId={inboxContactId}
                          showConversationEvents={showConversationEvents}
                        />
                      ),
                      (active) => (
                        <DeleteMessage
                          active={active}
                          messageId={message.id}
                          inboxContactId={inboxContactId}
                          showConversationEvents={showConversationEvents}
                        />
                      ),
                    ]}
                  />
                )}
              </div>
            </div>
          )}
        </span>
      </div>
    </>
  );
}

export default ConversationMessageContainer;
